// src/pages/FamilyHistoryModal.jsx
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { FiX } from "react-icons/fi";
import { BASE_URL } from "../utils";

const safeArr = (v) => (Array.isArray(v) ? v : []);
const toStr = (v) => (v == null ? "" : String(v));
const onlyDigits = (v) => toStr(v).replace(/\D+/g, "");

/* ---------- UI helpers ---------- */
const labelCls = "text-[11px] font-medium text-[var(--fg-muted)] tracking-wide";

const inputCls =
  "w-full min-w-0 h-8 rounded-md border border-slate-200 bg-white px-2.5 text-[12px] " +
  "text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 " +
  "focus:ring-blue-100 focus:border-blue-300";

const textareaCls =
  "w-full min-w-0 rounded-md border border-slate-200 bg-white px-2.5 py-2 text-[12px] " +
  "text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 " +
  "focus:ring-blue-100 focus:border-blue-300";

const thCls =
  "text-left px-3 py-2 text-[11px] font-medium text-[var(--fg-muted)]";
const tdCls = "px-3 py-2 align-top";

const btnBase =
  "h-8 px-3 rounded-md border border-slate-200 bg-white text-[12px] font-medium text-slate-700 " +
  "hover:bg-slate-50 transition-colors";

const btnPrimary =
  "h-8 px-3 rounded-md bg-blue-600 text-white text-[12px] font-semibold hover:bg-blue-700 " +
  "disabled:opacity-60 transition-colors";

/* ---------- parsing helpers ---------- */
function safeJsonParse(v) {
  try {
    if (!v) return null;
    if (typeof v === "object") return v;
    const s = String(v).trim();
    if (!s) return null;
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function normalizeFamily(record) {
  const fhRaw = record?.family_history;
  const parsed = safeJsonParse(fhRaw);

  const summary = toStr(parsed?.summary).trim();

  const relations = safeArr(parsed?.relations).map((r) => {
    const phones = [
      ...new Set(
        [...safeArr(r?.phones), toStr(r?.phone).trim()]
          .map((x) => onlyDigits(x))
          .filter(Boolean)
      ),
    ];

    const emails = [
      ...new Set([...safeArr(r?.emails), toStr(r?.email).trim()].filter(Boolean)),
    ];

    const addresses = [
      ...new Set([...safeArr(r?.addresses), toStr(r?.address).trim()].filter(Boolean)),
    ];

    const rowsCount = Math.max(1, phones.length, emails.length, addresses.length);

    const contactRows = Array.from({ length: rowsCount }).map((_, i) => ({
      phone: phones[i] || "",
      address: addresses[i] || "",
      email: emails[i] || "",
    }));

    return {
      name: toStr(r?.name),
      relationship: toStr(r?.relationship),
      contactRows,
    };
  });

  return {
    summary,
    relations: relations.length
      ? relations
      : [
          {
            name: "",
            relationship: "",
            contactRows: [{ phone: "", address: "", email: "" }],
          },
        ],
  };
}

function buildPayload(state) {
  const relations = safeArr(state.relations).map((m) => {
    const phones = safeArr(m.contactRows)
      .map((r) => onlyDigits(r.phone))
      .filter(Boolean);

    const emails = safeArr(m.contactRows)
      .map((r) => toStr(r.email).trim())
      .filter(Boolean);

    const addresses = safeArr(m.contactRows)
      .map((r) => toStr(r.address).trim())
      .filter(Boolean);

    return {
      name: toStr(m.name).trim() || null,
      relationship: toStr(m.relationship).trim() || null,
      phones,
      emails,
      addresses,
      phone: phones[0] || null,
      email: emails[0] || null,
      address: addresses[0] || null,
    };
  });

  return {
    summary: toStr(state.summary).trim() || "",
    relations,
  };
}

export default function FamilyHistoryModal({
  open,
  record,
  onClose,
  onSaved,
  apiBase = "/api/legacy",
}) {
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState({ summary: "", relations: [] });

  useEffect(() => {
    if (!open) return;
    setState(normalizeFamily(record));
  }, [open, record]);

  const updateMember = (idx, field, value) => {
    setState((prev) => ({
      ...prev,
      relations: safeArr(prev.relations).map((m, i) =>
        i === idx ? { ...m, [field]: value } : m
      ),
    }));
  };

  const updateContact = (mIdx, rowIdx, field, value) => {
    setState((prev) => ({
      ...prev,
      relations: safeArr(prev.relations).map((m, i) => {
        if (i !== mIdx) return m;
        const rows = safeArr(m.contactRows).map((r, ri) =>
          ri === rowIdx ? { ...r, [field]: value } : r
        );
        return { ...m, contactRows: rows };
      }),
    }));
  };

  const addContactRow = (mIdx) => {
    setState((prev) => ({
      ...prev,
      relations: safeArr(prev.relations).map((m, i) =>
        i === mIdx
          ? {
              ...m,
              contactRows: [
                ...safeArr(m.contactRows),
                { phone: "", address: "", email: "" },
              ],
            }
          : m
      ),
    }));
  };

  const removeContactRow = (mIdx, rowIdx) => {
    setState((prev) => ({
      ...prev,
      relations: safeArr(prev.relations).map((m, i) => {
        if (i !== mIdx) return m;
        const rows = safeArr(m.contactRows).filter((_, ri) => ri !== rowIdx);
        return {
          ...m,
          contactRows: rows.length ? rows : [{ phone: "", address: "", email: "" }],
        };
      }),
    }));
  };

  const addMember = () => {
    setState((prev) => ({
      ...prev,
      relations: [
        ...safeArr(prev.relations),
        {
          name: "",
          relationship: "",
          contactRows: [{ phone: "", address: "", email: "" }],
        },
      ],
    }));
  };

  const removeMember = (idx) => {
    setState((prev) => ({
      ...prev,
      relations: safeArr(prev.relations).filter((_, i) => i !== idx),
    }));
  };

  const handleSave = async () => {
    const id = record?.id;
    if (!id) return;

    const payload = buildPayload(state);

    setSaving(true);
    try {
      await axios.put(`${BASE_URL}${apiBase}/${id}/family_history`, {
        family_history: payload,
      });
      onSaved?.();
      onClose?.();
    } catch (e) {
      console.error("save family_history error", e);
      alert(e?.response?.data?.message || "Failed to save family history");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="w-full max-w-[1180px] max-h-[84vh] overflow-hidden rounded-2xl bg-white shadow-[0_20px_50px_rgba(15,23,42,0.18)]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3">
          <div>
            <div className="text-[14px] font-semibold text-slate-900">
              Family History (edit)
            </div>
            <div className="mt-0.5 text-[11px] text-slate-500">
              Add / edit / remove relationships + contacts and Save
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={onClose} className={btnBase}>
              Close
            </button>
            <button onClick={handleSave} disabled={saving} className={btnPrimary}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="max-h-[calc(84vh-68px)] overflow-auto px-5 py-4">
          {/* Summary */}
          <div className="mb-4">
            <div className={`${labelCls} mb-1`}>Summary</div>
            <textarea
              className={`${textareaCls} min-h-[70px] resize-y`}
              value={state.summary}
              onChange={(e) => setState((p) => ({ ...p, summary: e.target.value }))}
              placeholder="Optional summary..."
            />
          </div>

          {/* Add member */}
          <div className="mb-3">
            <button onClick={addMember} className={btnBase}>
              + Add Family Member
            </button>
          </div>

          {/* Table */}
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1120px] table-fixed text-sm">
                <colgroup>
                  <col style={{ width: "170px" }} />
                  <col style={{ width: "150px" }} />
                  <col style={{ width: "690px" }} />
                  <col style={{ width: "90px" }} />
                </colgroup>

                <thead className="border-b border-slate-200 bg-[var(--surface-muted)]">
                  <tr>
                    <th className={thCls}>Name</th>
                    <th className={thCls}>Relationship</th>
                    <th className={thCls}>Contacts (phone / address / email)</th>
                    <th className="px-3 py-2 text-center text-[11px] font-medium text-[var(--fg-muted)]">
                      Remove
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {safeArr(state.relations).map((m, idx) => (
                    <tr key={idx} className="border-b border-slate-100 last:border-b-0">
                      <td className={tdCls}>
                        <input
                          className={inputCls}
                          value={m.name}
                          onChange={(e) => updateMember(idx, "name", e.target.value)}
                          placeholder="Name"
                        />
                      </td>

                      <td className={tdCls}>
                        <input
                          className={inputCls}
                          value={m.relationship}
                          onChange={(e) =>
                            updateMember(idx, "relationship", e.target.value)
                          }
                          placeholder="Relation"
                        />
                      </td>

                      <td className={tdCls}>
                        <div className="p-0">
                          <div className="space-y-2.5">
                            {safeArr(m.contactRows).map((r, ri) => {
                              const onlyOne = safeArr(m.contactRows).length <= 1;

                              return (
                                <div key={ri} className="p-0">
                                  <div className="grid grid-cols-[120px_minmax(300px,1.9fr)_minmax(260px,1.25fr)] gap-2.5 items-start">
                                    {/* Phone */}
                                    <input
                                      className={`${inputCls} font-mono`}
                                      value={r.phone}
                                      inputMode="numeric"
                                      pattern="[0-9]*"
                                      maxLength={15}
                                      placeholder="Phone"
                                      onChange={(e) =>
                                        updateContact(
                                          idx,
                                          ri,
                                          "phone",
                                          onlyDigits(e.target.value)
                                        )
                                      }
                                      onKeyDown={(e) => {
                                        const allowedKeys = [
                                          "Backspace",
                                          "Delete",
                                          "ArrowLeft",
                                          "ArrowRight",
                                          "Tab",
                                        ];
                                        if (
                                          !allowedKeys.includes(e.key) &&
                                          !/^\d$/.test(e.key)
                                        ) {
                                          e.preventDefault();
                                        }
                                      }}
                                      onPaste={(e) => {
                                        e.preventDefault();
                                        const pasted = e.clipboardData.getData("text");
                                        updateContact(idx, ri, "phone", onlyDigits(pasted));
                                      }}
                                    />

                                    {/* Address */}
                                    <textarea
                                      className={`${textareaCls} min-h-[44px] resize-y`}
                                      value={r.address}
                                      onChange={(e) =>
                                        updateContact(idx, ri, "address", e.target.value)
                                      }
                                      placeholder="Address"
                                      rows={2}
                                    />

                                    {/* Email with floating red cross */}
                                    <div className="relative">
                                      <input
                                        className={`${inputCls} pr-8`}
                                        value={r.email}
                                        onChange={(e) =>
                                          updateContact(idx, ri, "email", e.target.value)
                                        }
                                        placeholder="Email"
                                        type="email"
                                      />

                                      {!onlyOne && (
                                        <button
                                          type="button"
                                          onClick={() => removeContactRow(idx, ri)}
                                          className="absolute right-2 top-2 text-red-500 hover:text-red-700"
                                          title="Remove row"
                                          aria-label="Remove contact row"
                                        >
                                          <FiX size={14} />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}

                            <div>
                              <button
                                type="button"
                                onClick={() => addContactRow(idx)}
                                className="inline-flex h-7 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
                              >
                                <span className="inline-flex h-4 w-4 items-center justify-center rounded bg-blue-600 text-[10px] text-white">
                                  +
                                </span>
                                Add contact row
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-3 py-2 text-center align-top">
                        <button
                          type="button"
                          onClick={() => removeMember(idx)}
                          className="h-7 rounded-md border border-slate-200 bg-white px-2.5 text-[11px] font-medium text-red-600 hover:bg-red-50"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}

                  {safeArr(state.relations).length === 0 && (
                    <tr>
                      <td className="p-4 text-slate-500" colSpan={4}>
                        No family members
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={onClose} className={btnBase}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} className={btnPrimary}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

